import { DateTime } from '@ksyos/date-time';
import { Text } from '@ksyos/design-system';
import { useNavigate, useParams, useSearchParams } from '@ksyos/router';
import { useEffect, useState } from 'react';
import type { ActivityType, ServiceCode, ServiceType } from '@common/enums';
import type {
    IServiceConsumerGetOne,
    IServiceGroupSettingsGetOne,
    IServiceOrganizationSettingsGetOne,
    IServiceProviderGetOne,
    IServiceSupervisorGetOne,
} from '@common/interfaces/';
import type { ValidationErrors } from '@frontend/utils';
import { getServiceLabel } from '@common/customControls/getServiceLabel';
import { CompensationPayoutEntity, ManagerAuthorization } from '@common/enums';
import { getAllowedCompensationsByServiceAndEntity } from '@common/utils/getAllowedCompensationsByServiceAndEntity';
import { getDisplayName } from '@common/utils/getDisplayName';
import { compensationPayoutAddSchema, compensationPayoutEditSchema } from '@common/validation/compensationPayout';
import { fetchJson } from '@frontend/store/fetch';
import { translateKey } from '@frontend/translate';
import {
    assertHasAuthorization,
    currencyFormatter,
    emptyValidationErrors,
    hasOneAuthorization,
    OverlapChecker,
    RateLimiter,
    updateValidationErrorsForOne,
    validate,
} from '@frontend/utils';
import type { IBreadcrumbItem } from '../../elements/common';
import type { ICompensationPayout } from '../../interfaces/compensationPayout';
import type { ICompensationPayoutWithErrors } from '../personalSettings/activityCompensationPayouts/helpers';
import {
    Button,
    DateInput,
    Dropdown,
    FormContainer,
    FormGroup,
    FormHeader,
    FormLabel,
    Header,
    InputCurrency,
    InputGroup,
    Loading,
    OverlapMessage,
    SaveSubmit,
} from '../../elements/common';
import { FixedWidthTemplate } from '../../templates/FixedWidthTemplate';
import { validateDateBoundary } from '../personalSettings/activityCompensationPayouts/helpers';

interface ICheckOverlapParams {
    [key: string]: string | null | undefined;
    serviceConsumerId?: string;
    serviceGroupSettingId?: string;
    serviceOrganizationSettingId?: string;
    serviceProviderId?: string;
    serviceSupervisorId?: string;
    type: ActivityType;
}

interface IOverlap {
    endDate: string | null;
    id: string;
    startDate: string;
}

export function CompensationPayoutAddEdit() {
    assertHasAuthorization([
        ManagerAuthorization.groupAddEdit,
        ManagerAuthorization.organizationRead,
        ManagerAuthorization.serviceRightsCareproviders,
        ManagerAuthorization.serviceRightsGroups,
        ManagerAuthorization.serviceRightsOrganizations,
    ]);

    const atMostOncePerSecond = new RateLimiter(1000);
    const overlapChecker = new OverlapChecker('/api/compensationPayouts');
    const navigate = useNavigate();
    const { id } = useParams();
    const [searchParams] = useSearchParams();
    const serviceSupervisorId = searchParams.getOptional('serviceSupervisorId');
    const serviceOrganizationSettingId = searchParams.getOptional('serviceOrganizationSettingId');
    const serviceProviderId = searchParams.getOptional('serviceProviderId');
    const serviceConsumerId = searchParams.getOptional('serviceConsumerId');
    const serviceGroupSettingId = searchParams.getOptional('serviceGroupSettingId');
    const serviceType = searchParams.getOptional('serviceType') as ServiceType;
    const serviceCode = searchParams.getOptional('serviceCode') as ServiceCode;
    const serviceId = searchParams.getOptional('serviceId');
    const groupId = searchParams.getOptional('groupId');
    const practitionerId = searchParams.getOptional('practitionerId');
    const organizationId = searchParams.getOptional('organizationId');

    const [compensationPayout, setCompensationPayout] = useState<ICompensationPayout>();
    const [minimumStartDate, setMinimumStartDate] = useState<string | undefined>();
    const [maximumEndDate, setMaximumEndDate] = useState<string | undefined>();
    const [overlap, setOverlap] = useState<IOverlap | undefined>();
    const [validationErrors, setValidationErrors] = useState<ValidationErrors>(emptyValidationErrors);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [serviceGroupSettings, setServiceGroupSettings] = useState<IServiceGroupSettingsGetOne>();
    const [serviceSupervisor, setServiceSupervisor] = useState<IServiceSupervisorGetOne>();
    const [serviceProvider, setServiceProvider] = useState<IServiceProviderGetOne>();
    const [serviceConsumer, setServiceConsumer] = useState<IServiceConsumerGetOne>();
    const [serviceOrganizationSettings, setServiceOrganizationSettings] =
        useState<IServiceOrganizationSettingsGetOne>();

    const canEdit = hasOneAuthorization([
        ManagerAuthorization.groupAddEdit,
        ManagerAuthorization.organizationAddEdit,
        ManagerAuthorization.serviceRightsCareproviders,
        ManagerAuthorization.serviceRightsGroups,
        ManagerAuthorization.serviceRightsOrganizations,
    ]);

    useEffect(() => {
        setup();
    }, [id]);

    const headerText =
        (isEdit() ? translateKey('common.edit') : translateKey('common.new')) +
        ' ' +
        translateKey('compensationPayouts.fees');

    let entity: CompensationPayoutEntity | null = null;
    if (serviceSupervisorId) {
        entity = CompensationPayoutEntity.serviceSupervisor;
    } else if (serviceConsumerId) {
        entity = CompensationPayoutEntity.serviceConsumer;
    } else if (serviceGroupSettingId) {
        entity = CompensationPayoutEntity.serviceGroupSetting;
    } else if (serviceOrganizationSettingId) {
        entity = CompensationPayoutEntity.serviceOrganizationSetting;
    } else if (serviceProviderId) {
        entity = CompensationPayoutEntity.serviceProvider;
    }

    let availableActivityTypes: ActivityType[] = [];

    if (entity) {
        availableActivityTypes = getAllowedCompensationsByServiceAndEntity(serviceType, serviceCode, entity);
    }

    return (
        <FixedWidthTemplate breadcrumb={getBreadcrumb()}>
            <Header content={headerText} />

            <Loading
                item={compensationPayout}
                loaded={(compensation) => (
                    <FormContainer onSubmit={async () => await save()}>
                        <FormHeader>
                            {`${headerText}: ${getServiceLabel(serviceCode, 'request', 'workList')} ${translateKey(
                                'common.for'
                            )} ${serviceGroupSettings?.group.name}`}
                        </FormHeader>

                        {overlap && (
                            <OverlapMessage
                                endDate={overlap.endDate}
                                link={getCompensationPayoutEditUrl(overlap.id)}
                                message={translateKey('common.overlapMessage')}
                                startDate={overlap.startDate}
                            />
                        )}
                        <FormGroup hasErrors={validationErrors.has('startDate')}>
                            <FormLabel isRequired>{translateKey('common.startDate')}</FormLabel>
                            <InputGroup>
                                {!canEdit ? (
                                    renderText(compensation.startDate)
                                ) : (
                                    <DateInput
                                        error={validationErrors.get('startDate')}
                                        maxDate={maximumEndDate}
                                        minDate={minimumStartDate}
                                        onChange={async (value) => await handleChange('startDate', value ?? '')}
                                        onDoValidate={() => validateOne('startDate')}
                                        value={compensation.startDate}
                                    />
                                )}
                            </InputGroup>
                        </FormGroup>

                        <FormGroup hasErrors={validationErrors.has('endDate')}>
                            <FormLabel>{translateKey('common.endDate')}</FormLabel>
                            <InputGroup>
                                {!canEdit ? (
                                    renderText(compensation.endDate)
                                ) : (
                                    <DateInput
                                        error={validationErrors.get('endDate')}
                                        maxDate={maximumEndDate}
                                        minDate={minimumStartDate}
                                        onChange={async (value) => await handleChange('endDate', value)}
                                        onDoValidate={() => validateOne('endDate')}
                                        value={compensation.endDate}
                                    />
                                )}
                            </InputGroup>
                        </FormGroup>

                        <FormGroup hasErrors={validationErrors.has('amount')}>
                            <FormLabel isRequired>{translateKey('compensationPayouts.amount')}</FormLabel>
                            <InputGroup>
                                {!canEdit ? (
                                    renderText(compensation.amount ? currencyFormatter(compensation.amount / 100) : '')
                                ) : (
                                    <InputCurrency
                                        error={validationErrors.get('amount')}
                                        onChange={async (value) => handleChange('amount', value)}
                                        onDoValidate={async () => await validateOne('amount')}
                                        value={compensation.amount}
                                    />
                                )}
                            </InputGroup>
                        </FormGroup>

                        <FormGroup hasErrors={validationErrors.has('type')}>
                            <FormLabel isRequired>{translateKey('common.activity')}</FormLabel>
                            <InputGroup>
                                {!canEdit ? (
                                    renderText(translateKey(`activityTypeEnum.${compensation.type}`))
                                ) : (
                                    <Dropdown
                                        error={validationErrors.get('type')}
                                        onChange={async (value) => await handleChange('type', value!)}
                                        onDoValidate={async () => await validateOne('type')}
                                        selectOptions={availableActivityTypes.map((activity) => {
                                            return {
                                                text: translateKey(`activityTypeEnum.${activity}`),
                                                value: activity,
                                            };
                                        })}
                                        value={compensation.type}
                                    />
                                )}
                            </InputGroup>
                        </FormGroup>

                        <Button
                            color="secondary"
                            disabled={isSaving}
                            large
                            left
                            onClick={() => navigate(-1)}
                            outlined
                        >
                            {translateKey(canEdit ? 'common.cancel' : 'common.goBack')}
                        </Button>
                        {canEdit && <SaveSubmit disabled={isSaving || !!overlap} />}
                    </FormContainer>
                )}
            />
        </FixedWidthTemplate>
    );

    async function loadCompensationPayout(compensationPayoutId: string) {
        const url = `/api/compensationPayouts/${compensationPayoutId}`;
        const result = await fetchJson(url, 'GET');
        if (result.json) {
            checkOverlap(result.json);
            setCompensationPayout(result.json);
        }
    }

    function isEdit() {
        return !!id;
    }

    function getSchema() {
        return isEdit() ? compensationPayoutEditSchema : compensationPayoutAddSchema;
    }

    async function setup() {
        setIsSaving(false);
        setMinimumStartAndMaximumEndDate();
        if (id) {
            await loadCompensationPayout(id);
        } else {
            const newCompensation = {
                amount: 0,
                endDate: null,
                serviceConsumerId: serviceConsumerId,
                serviceGroupSettingId: serviceGroupSettingId,
                serviceOrganizationSettingId: serviceOrganizationSettingId,
                serviceProviderId: serviceProviderId,
                serviceSupervisorId: serviceSupervisorId,
                startDate: DateTime.now().format('YYYY-MM-DD'),
                type: availableActivityTypes[0],
            };
            setCompensationPayout(newCompensation);
            await checkOverlap(newCompensation);
        }

        if (serviceSupervisorId) {
            loadServiceSupervisor();
        } else if (serviceConsumerId) {
            loadServiceConsumer();
        } else if (serviceProviderId) {
            loadServiceProvider();
        } else if (serviceGroupSettingId) {
            loadServiceGroupSetting();
        } else if (serviceOrganizationSettingId) {
            loadServiceOrganizationSettings();
        }
    }

    async function setMinimumStartAndMaximumEndDate() {
        let url;
        if (serviceSupervisorId) {
            url = `/api/serviceSupervisors/${serviceSupervisorId}`;
        } else if (serviceOrganizationSettingId) {
            url = `/api/serviceOrganizationSettings/${serviceOrganizationSettingId}`;
        } else if (serviceProviderId) {
            url = `/api/serviceProviders/${serviceProviderId}`;
        } else if (serviceConsumerId) {
            url = `/api/serviceConsumers/${serviceConsumerId}`;
        } else if (serviceGroupSettingId) {
            url = `/api/serviceGroupSettings/${serviceGroupSettingId}`;
        }

        if (url) {
            const result = await fetchJson(url, 'GET');
            if (result.json?.startDate) {
                setMinimumStartDate(result.json.startDate);
            }
            if (result.json?.endDate) {
                setMaximumEndDate(result.json.endDate);
            }
        }
    }

    async function handleChange<K extends keyof ICompensationPayout>(name: K, value: ICompensationPayout[K]) {
        if (compensationPayout) {
            const newCompensationPayout = { ...compensationPayout, [name]: value };
            setCompensationPayout(newCompensationPayout);

            if (name === 'startDate' || name === 'endDate' || name === 'type') {
                checkOverlap(newCompensationPayout);
                const dateErrors = validateDateBoundary(
                    { ...compensationPayout, errors: validationErrors } as ICompensationPayoutWithErrors,
                    minimumStartDate,
                    maximumEndDate
                );
                setValidationErrors(dateErrors.errors);
            }
        }
    }

    async function validateOne(name: keyof ICompensationPayout) {
        setValidationErrors(updateValidationErrorsForOne(getSchema(), compensationPayout, validationErrors, name));
    }

    async function checkOverlap(compensation: ICompensationPayout) {
        atMostOncePerSecond.callMeMaybe(async () => {
            const checkParams: ICheckOverlapParams = {
                type: compensation.type,
            };

            if (serviceSupervisorId) {
                checkParams.serviceSupervisorId = serviceSupervisorId;
            } else if (serviceOrganizationSettingId) {
                checkParams.serviceOrganizationSettingId = serviceOrganizationSettingId;
            } else if (serviceProviderId) {
                checkParams.serviceProviderId = serviceProviderId;
            } else if (serviceConsumerId) {
                checkParams.serviceConsumerId = serviceConsumerId;
            } else if (serviceGroupSettingId) {
                checkParams.serviceGroupSettingId = serviceGroupSettingId;
            }

            const isOverlapped = await overlapChecker.check(
                checkParams,
                compensation.startDate,
                compensation.endDate,
                id
            );
            setOverlap(isOverlapped);
        });
    }

    async function save() {
        if (overlap) {
            return;
        }

        const { errors, item } = validate(getSchema(), compensationPayout);
        const { errors: updatedErrors } = validateDateBoundary(
            { ...compensationPayout, errors } as ICompensationPayoutWithErrors,
            minimumStartDate,
            maximumEndDate
        );

        setValidationErrors(updatedErrors);
        if (updatedErrors.size > 0) {
            return;
        }
        let url = '/api/compensationPayouts';
        if (isEdit()) {
            url += `/${id}`;
        }
        setIsSaving(true);

        await fetchJson(url, 'POST', item);

        setIsSaving(false);

        navigate(-1);
    }

    function getCompensationPayoutEditUrl(compensationId: string) {
        const params = new URLSearchParams({
            ...(serviceType ? { serviceType } : {}),
            ...(serviceCode ? { serviceCode } : {}),
        });
        if (serviceSupervisorId) {
            params.append('serviceSupervisorId', serviceSupervisorId);
        } else if (serviceOrganizationSettingId) {
            params.append('serviceOrganizationSettingId', serviceOrganizationSettingId);
        } else if (serviceProviderId) {
            params.append('serviceProviderId', serviceProviderId);
        } else if (serviceConsumerId) {
            params.append('serviceConsumerId', serviceConsumerId);
        } else if (serviceGroupSettingId) {
            params.append('serviceGroupSettingId', serviceGroupSettingId);
        }

        return `/compensationPayouts/${compensationId}?${params.toString()}`;
    }

    async function loadServiceGroupSetting() {
        const url = `/api/serviceGroupSettings/${serviceGroupSettingId}`;
        const result = await fetchJson(url, 'GET');
        setServiceGroupSettings(result.json);
    }

    async function loadServiceSupervisor() {
        const url = `/api/serviceSupervisors/${serviceSupervisorId}`;
        const result = await fetchJson(url, 'GET');
        setServiceSupervisor(result.json);
    }

    async function loadServiceProvider() {
        const url = `/api/serviceProviders/${serviceProviderId}`;
        const result = await fetchJson(url, 'GET');
        setServiceProvider(result.json);
    }

    async function loadServiceConsumer() {
        const url = `/api/serviceConsumers/${serviceConsumerId}`;
        const result = await fetchJson(url, 'GET');
        setServiceConsumer(result.json);
    }

    async function loadServiceOrganizationSettings() {
        const url = `/api/serviceOrganizationSettings/${serviceOrganizationSettingId}`;
        const result = await fetchJson(url, 'GET');
        setServiceOrganizationSettings(result.json);
    }

    function getBreadcrumb() {
        let breadcrumb: IBreadcrumbItem[] = [];

        if (serviceGroupSettingId && serviceGroupSettings) {
            if (groupId) {
                const returnUrl = `/groups/${groupId}`;
                breadcrumb = [
                    { text: serviceGroupSettings.group.name, url: returnUrl },
                    { text: serviceGroupSettings.service.name, url: returnUrl },
                ];
            } else if (serviceId) {
                const returnUrl = `/services/${serviceId}?tab=groupSettings`;
                breadcrumb = [
                    { text: serviceGroupSettings.service.name, url: returnUrl },
                    { text: serviceGroupSettings.group.name, url: returnUrl },
                ];
            }
        } else if (serviceSupervisorId && serviceSupervisor) {
            if (practitionerId) {
                const returnUrl = `/practitioners/${practitionerId}?tab=serviceSupervisorsListIsVisible`;
                breadcrumb = [
                    { text: getDisplayName(serviceSupervisor.employment.practitioner), url: returnUrl },
                    { text: serviceSupervisor.employment.organization.name, url: returnUrl },
                    { text: serviceSupervisor.service.name, url: returnUrl },
                ];
            } else if (serviceId) {
                const returnUrl = `/services/${serviceId}?tab=supervisors`;
                breadcrumb = [
                    { text: serviceSupervisor.service.name, url: returnUrl },
                    { text: serviceSupervisor.employment.organization.name, url: returnUrl },
                    { text: getDisplayName(serviceSupervisor.employment.practitioner), url: returnUrl },
                ];
            }
        } else if (serviceProviderId && serviceProvider) {
            if (serviceId) {
                const returnUrl = `/services/${serviceId}?tab=providers`;
                breadcrumb = [
                    { text: serviceProvider.service.name, url: returnUrl },
                    { text: serviceProvider.employment.organization.name, url: returnUrl },
                    { text: getDisplayName(serviceProvider.employment.practitioner), url: returnUrl },
                ];
            } else if (practitionerId) {
                const returnUrl = `/practitioners/${practitionerId}?tab=servicesProvidingListIsVisible`;
                breadcrumb = [
                    { text: getDisplayName(serviceProvider.employment.practitioner), url: returnUrl },
                    { text: serviceProvider.employment.organization.name, url: returnUrl },
                    { text: serviceProvider.service.name, url: returnUrl },
                ];
            }
        } else if (serviceConsumerId && serviceConsumer) {
            if (serviceId) {
                const returnUrl = `/services/${serviceId}?tab=consumers`;
                breadcrumb = [
                    { text: serviceConsumer.service.name, url: returnUrl },
                    { text: serviceConsumer.employment.organization.name, url: returnUrl },
                    { text: getDisplayName(serviceConsumer.employment.practitioner), url: returnUrl },
                ];
            } else if (practitionerId) {
                const returnUrl = `/practitioners/${practitionerId}?tab=servicesConsumingListIsVisible`;
                breadcrumb = [
                    { text: getDisplayName(serviceConsumer.employment.practitioner), url: returnUrl },
                    { text: serviceConsumer.employment.organization.name, url: returnUrl },
                    { text: serviceConsumer.service.name, url: returnUrl },
                ];
            }
        } else if (serviceOrganizationSettingId && serviceOrganizationSettings) {
            if (serviceId) {
                const returnUrl = `/services/${serviceId}?tab=providerOrganizationSettings`;
                breadcrumb = [
                    { text: serviceOrganizationSettings.service.name, url: returnUrl },
                    { text: serviceOrganizationSettings.organization.name, url: returnUrl },
                ];
            } else if (organizationId) {
                const returnUrl = `/organizations/${organizationId}?tab=serviceOrganizationSettings`;
                breadcrumb = [
                    { text: serviceOrganizationSettings.organization.name, url: returnUrl },
                    { text: serviceOrganizationSettings.service.name, url: returnUrl },
                ];
            }
        }

        breadcrumb.push({
            text: headerText,
        });

        return breadcrumb;
    }

    function renderText(content: string | null) {
        return (
            <Text
                as="p"
                fontWeight="medium"
            >
                {content}
            </Text>
        );
    }
}
